/**
 * Work detail panel: glass overlay populated from the works data,
 * with hash deep-linking (#/work/<slug>).
 */
export function createPanel({ onClose }) {
  const root = document.getElementById("xpPanel");
  const closeBtn = document.getElementById("xpPanelClose");
  const yearEl = document.getElementById("xpPanelYear");
  const titleEl = document.getElementById("xpPanelTitle");
  const mediumEl = document.getElementById("xpPanelMedium");
  const mediaEl = document.getElementById("xpPanelMedia");
  const descEl = document.getElementById("xpPanelDesc");
  const metaEl = document.getElementById("xpPanelMeta");
  const actionsEl = document.getElementById("xpPanelActions");

  let openSlug = null;

  function metaRow(label, value) {
    const div = document.createElement("div");
    const dt = document.createElement("dt");
    dt.textContent = label;
    const dd = document.createElement("dd");
    dd.textContent = value;
    div.append(dt, dd);
    return div;
  }

  function buildMedia(work) {
    mediaEl.innerHTML = "";
    mediaEl.hidden = false;

    if (work.youtube) {
      const wrap = document.createElement("div");
      wrap.className = "video-embed";
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.youtube-nocookie.com/embed/${work.youtube}`;
      iframe.title = work.title;
      iframe.loading = "lazy";
      iframe.allow =
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
      iframe.allowFullscreen = true;
      wrap.appendChild(iframe);
      mediaEl.appendChild(wrap);
      return;
    }

    if (work.image) {
      const img = document.createElement("img");
      // Downscaled variant (see scripts/build-textures.mjs): decoding a
      // 4K original mid-render caused 100-200ms frame spikes. Falls
      // back to the original if the variant is missing.
      const variant =
        "/assets/tex/" +
        work.image
          .replace(/^\/assets\//, "")
          .replace(/\//g, "__")
          .replace(/\.[a-z0-9]+$/i, "") +
        ".jpg";
      img.src = variant;
      img.onerror = () => {
        img.onerror = null;
        img.src = work.image;
      };
      img.alt = work.title;
      img.decoding = "async";
      mediaEl.appendChild(img);
      return;
    }

    mediaEl.hidden = true;
  }

  function open(work, { replaceHash = false } = {}) {
    openSlug = work.slug;

    yearEl.textContent = work.year ? String(work.year) : "";
    titleEl.textContent = work.title || "";
    mediumEl.textContent = work.medium || "";
    mediumEl.hidden = !work.medium;

    buildMedia(work);

    descEl.innerHTML = "";
    if (work.descriptionHtml) {
      descEl.innerHTML = work.descriptionHtml;
    } else if (work.description) {
      const P = document.createElement("p");
      P.textContent = work.description;
      descEl.appendChild(P);
    }

    metaEl.innerHTML = "";
    if (work.location) metaEl.appendChild(metaRow("Location", work.location));
    if (work.dimensions) {
      metaEl.appendChild(metaRow("Dimensions", work.dimensions));
    } else if (work.dimensionsVariable) {
      metaEl.appendChild(metaRow("Dimensions", "Dimensions variable"));
    } else if (work.height && work.width) {
      const dims = `${work.height} × ${work.width}${work.depth ? ` × ${work.depth}` : ""} cm`;
      metaEl.appendChild(metaRow("Dimensions", dims));
    }
    if (work.categories?.length) {
      metaEl.appendChild(metaRow("Tags", work.categories.join(", ")));
    }
    metaEl.hidden = !metaEl.children.length;

    actionsEl.innerHTML = "";
    if (work.instagram) {
      const igLink = document.createElement("a");
      igLink.className = "button";
      igLink.href = work.instagram;
      igLink.target = "_blank";
      igLink.rel = "noopener noreferrer";
      igLink.textContent = "Watch on Instagram";
      actionsEl.appendChild(igLink);
    }
    if (work.link) {
      const ext = document.createElement("a");
      ext.className = "button";
      ext.href = work.link;
      ext.target = "_blank";
      ext.rel = "noopener noreferrer";
      ext.textContent = work.linkLabel || "Visit the site →";
      actionsEl.appendChild(ext);
    }
    const page = document.createElement("a");
    page.className = "button";
    page.href = work.url;
    page.textContent = "Full page";
    actionsEl.appendChild(page);

    root.hidden = false;
    // Next frame so the transition runs.
    requestAnimationFrame(() => {
      root.classList.add("is-open");
      root.scrollTop = 0;
    });

    const target = `#/work/${work.slug}`;
    if (location.hash !== target) {
      // Browsing work-to-work replaces the entry so Back exits the
      // detail view instead of stepping through every visited work.
      if (replaceHash) history.replaceState(null, "", target);
      else history.pushState(null, "", target);
    }
  }

  function close({ silent = false } = {}) {
    if (!openSlug) return;
    openSlug = null;
    root.classList.remove("is-open");
    setTimeout(() => {
      if (!openSlug) {
        root.hidden = true;
        mediaEl.innerHTML = ""; // stop embedded video playback
      }
    }, 600);
    if (!silent && location.hash.startsWith("#/work/")) {
      history.pushState(null, "", location.pathname + location.search);
    }
    onClose?.();
  }

  closeBtn.addEventListener("click", () => close());
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });

  return {
    open,
    close,
    get openSlug() {
      return openSlug;
    }
  };
}
